/**
 * Visual CMS — curated lucide icon registry.
 *
 * The full `lucide-react` package exports 1000+ icons; importing all by name
 * via `lucide-react/dynamicIconImports` blows up the bundle. We hand-pick a
 * ~120-icon set that covers the use cases for Visual CMS cards / hero
 * decorations / trust blocks.
 *
 * Admins select from this list in the icon picker; the renderer's <SafeIcon>
 * resolves the key to a component and falls back to a placeholder if a stored
 * key is not registered (handles the case where a Phase-2 admin typed a free
 * icon name that we don't include here).
 */

import {
  type LucideIcon,
  ArrowRight,
  Award,
  Baby,
  BadgeCheck,
  Bell,
  Bike,
  Bolt,
  Book,
  Bookmark,
  Briefcase,
  Building,
  Calendar,
  CalendarDays,
  Camera,
  Car,
  CheckCircle,
  CheckSquare,
  ChevronDown,
  Circle,
  Clock,
  Cloud,
  Coffee,
  Compass,
  Cookie,
  CreditCard,
  Database,
  DollarSign,
  Download,
  Edit,
  Eye,
  Facebook,
  File,
  Filter,
  Flag,
  Folder,
  Gift,
  Globe,
  HandCoins,
  Hand,
  Handshake,
  HardHat,
  Headphones,
  Heart,
  HelpCircle,
  Home,
  Image as ImageIcon,
  Inbox,
  Info,
  Instagram,
  Key,
  KeyRound,
  Laptop,
  LayoutGrid,
  LifeBuoy,
  Lightbulb,
  Link as LinkIcon,
  List,
  Lock,
  Mail,
  Map,
  MapPin,
  MessageCircle,
  MessageSquare,
  Mic,
  Monitor,
  Moon,
  MoreHorizontal,
  MousePointer,
  Music,
  Package,
  Paperclip,
  Pause,
  Pencil,
  Phone,
  PieChart,
  Plane,
  Play,
  Plug,
  Plus,
  Power,
  Printer,
  Recycle,
  RefreshCw,
  Save,
  Scale,
  Scissors,
  Send,
  Server,
  Settings,
  Share2,
  Shield,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Smartphone,
  Smile,
  Sparkles,
  Speaker,
  Star,
  Store,
  Sun,
  Tag,
  Target,
  Terminal,
  ThumbsUp,
  Timer,
  Trash2,
  TrendingUp,
  Truck,
  Twitter,
  Umbrella,
  User,
  UserCheck,
  Users,
  Video,
  Volume2,
  Wallet,
  Watch,
  Wifi,
  Wrench,
  Youtube,
  Zap,
} from "lucide-react";

export const VISUAL_CMS_ICON_REGISTRY: Record<string, LucideIcon> = {
  ArrowRight,
  Award,
  Baby,
  BadgeCheck,
  Bell,
  Bike,
  Bolt,
  Book,
  Bookmark,
  Briefcase,
  Building,
  Calendar,
  CalendarDays,
  Camera,
  Car,
  CheckCircle,
  CheckSquare,
  ChevronDown,
  Circle,
  Clock,
  Cloud,
  Coffee,
  Compass,
  Cookie,
  CreditCard,
  Database,
  DollarSign,
  Download,
  Edit,
  Eye,
  Facebook,
  File,
  Filter,
  Flag,
  Folder,
  Gift,
  Globe,
  HandCoins,
  Hand,
  Handshake,
  HardHat,
  Headphones,
  Heart,
  HelpCircle,
  Home,
  Image: ImageIcon,
  Inbox,
  Info,
  Instagram,
  Key,
  KeyRound,
  Laptop,
  LayoutGrid,
  LifeBuoy,
  Lightbulb,
  Link: LinkIcon,
  List,
  Lock,
  Mail,
  Map,
  MapPin,
  MessageCircle,
  MessageSquare,
  Mic,
  Monitor,
  Moon,
  MoreHorizontal,
  MousePointer,
  Music,
  Package,
  Paperclip,
  Pause,
  Pencil,
  Phone,
  PieChart,
  Plane,
  Play,
  Plug,
  Plus,
  Power,
  Printer,
  Recycle,
  RefreshCw,
  Save,
  Scale,
  Scissors,
  Send,
  Server,
  Settings,
  Share2,
  Shield,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Smartphone,
  Smile,
  Sparkles,
  Speaker,
  Star,
  Store,
  Sun,
  Tag,
  Target,
  Terminal,
  ThumbsUp,
  Timer,
  Trash2,
  TrendingUp,
  Truck,
  Twitter,
  Umbrella,
  User,
  UserCheck,
  Users,
  Video,
  Volume2,
  Wallet,
  Watch,
  Wifi,
  Wrench,
  Youtube,
  Zap,
};

export const VISUAL_CMS_ICON_KEYS: string[] = Object.keys(VISUAL_CMS_ICON_REGISTRY).sort();

export function getVisualCmsIcon(key: string | null | undefined): LucideIcon | null {
  if (!key) return null;
  return VISUAL_CMS_ICON_REGISTRY[key] ?? null;
}
